/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/UserEditView/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import React, { useState, useRef, useEffect, useMemo, useContext } from "react";
import { useNavigate } from "react-router";
import {
  validateEmail,
  validateMobilePhoneNumber,
  validateRFC,
} from "../../utils/common.js";
import AssetImage from "../AssetImage";
import Select from "react-select";
import styles from "./UserEditView.module.css";
import CircularProgress from "@mui/material/CircularProgress";
import { SvgIcon } from "@mui/material";
import LanguageSelector from "../LanguageSelector";
import NotificationBell from "../NotificationBell/index.jsx";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { updateProfileImage } from "../../redux/profileSlice";
import {
  getUser,
  getUserRoles,
  updateUser,
  getUserIdEditCapabilities,
  filterUserRolesForEdit,
} from "../../logic/user/index.js";
import {
  rolesWithAssignedRole,
  selectedEditableRole,
} from "../../logic/user/editableRoleSelection.js";
import { AppContext } from "../../context/AppContext.js";

import { ReactComponent as DeleteIcon } from "../../assets/icons/UserViews/DeleteIcon.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/UserViews/EditIcon.svg";
import { ReactComponent as UserNameIcon } from "../../assets/icons/UserViews/UserNameIcon.svg";
import { ReactComponent as TitleIcon } from "../../assets/icons/UserViews/TitleIcon.svg";
import { ReactComponent as EmailIcon } from "../../assets/icons/UserViews/EmailIcon.svg";
import { ReactComponent as RFCIcon } from "../../assets/icons/UserViews/RFCIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../../assets/icons/UserViews/ChevronLeftIcon.svg";

/**
 * Full-page view for editing an existing user on mobile/tablet. Allows updating avatar,
 * user type, name, title, email, phone number, and RFC with validation. Mirrors UserEditModal behavior.
 * Used when the app is in mobile/tablet layout and the user chooses to edit a user.
 *
 * @param {Object} props
 * @param {Object} props.user - The user being edited
 * @param {function} props.onBack - Called when the user navigates back
 * @param {function} props.onUpdateUser - Called with the updated user when save succeeds
 * @param {Object} [props.currentUser] - The currently logged-in user; used for permission checks (e.g. primary user RFC).
 */
const UserEditView = ({ user, onBack, onUpdateUser, currentUser }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { permissions } = useContext(AppContext) ?? {};
  const profilePic = useSelector(() => user.imagenDePerfil);

  const [tempImage, setTempImage] = useState(null);
  const [imageKey, setImageKey] = useState(Date.now());
  const [roles, setRoles] = useState([]);
  const [tempUser, setTempUser] = useState({
    tipoDeUsuario: "",
    nombre: "",
    titulo: "",
    correoElectronico: "",
    numeroCelular: "",
    RFC: "",
    imagenDePerfil: "",
    imagenDePerfilFormato: "",
    activo: "",
  });
  const [imagenDePerfil, setProfileImage] = useState(null);
  const [imagenDePerfilFormato, setProfileImageFormat] = useState("");
  const [loading, setLoading] = useState(false);
  const [RFCError, setRFCError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [emailFormatError, setEmailFormatError] = useState(null);
  const [phoneFormatError, setPhoneFormatError] = useState(null);
  const [rfcFormatError, setRfcFormatError] = useState(null);
  const [isAvatarDeleted, setIsAvatarDeleted] = useState(false);
  const fileInputRef = useRef(null);
  const [currentUserData, setCurrentUserData] = useState(null);

  /* --- Data fetching --- */

  /**
   * Fetches the current user by ID and stores it in state (used for permission checks).
   * Used to determine primary user and RFC editability.
   */
  const getUserData = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      return;
    }
    const userData = await getUser(userId, navigate);
    setCurrentUserData(userData ?? null);
  };

  /* --- Validation and submit --- */

  /**
   * Runs format validation on email, phone (if filled), and RFC (if filled). Sets format error state and returns whether all are valid.
   * @returns {boolean} True if all filled fields pass validation.
   * Sets format error state for email, phone, and RFC as a side effect. Used before submit.
   */
  const validateFormFields = () => {
    let valid = true;
    if (tempUser.correoElectronico.trim() !== "") {
      const emailResult = validateEmail(tempUser.correoElectronico);
      if (!emailResult.isValid) {
        setEmailFormatError(emailResult.error);
        valid = false;
      } else {
        setEmailFormatError(null);
      }
    } else {
      setEmailFormatError(null);
    }
    if (tempUser.numeroCelular?.trim() !== "") {
      const phoneResult = validateMobilePhoneNumber(tempUser.numeroCelular);
      if (!phoneResult.isValid) {
        setPhoneFormatError(phoneResult.error);
        valid = false;
      } else {
        setPhoneFormatError(null);
      }
    } else {
      setPhoneFormatError(null);
    }
    if (tempUser.RFC?.trim() !== "") {
      const rfcResult = validateRFC(tempUser.RFC);
      if (!rfcResult.isValid) {
        setRfcFormatError(rfcResult.error);
        valid = false;
      } else {
        setRfcFormatError(null);
      }
    } else {
      setRfcFormatError(null);
    }
    return valid;
  };

  /**
   * Validates form, then updates the user via API. On success calls onUpdateUser and onBack; on conflict sets RFCError or emailError.
   */
  const handleUpdateUser = async () => {
    if (
      !validateFormFields() ||
      !tempUser.correoElectronico?.trim() ||
      !tempUser.tipoDeUsuario
    ) {
      return;
    }

    const isPrimaryUser = currentUserData?.esColaborador === false;
    if (isPrimaryUser && !tempUser.RFC?.trim()) {
      setRFCError(true);
      return;
    }

    if (!user._id) {
      logger.error("User ID is missing.");
      return;
    }

    const updatedUserData = {
      ...tempUser,
      imagenDePerfil: isAvatarDeleted ? "" : imagenDePerfil,
      imagenDePerfilFormato: isAvatarDeleted ? "" : imagenDePerfilFormato,
    };

    setLoading(true);
    setImageKey(Date.now());

    try {
      await updateUser(user._id, updatedUserData, {
        navigate,
        onSuccess: (updatedUser) => {
          setImageKey(Date.now());
          dispatch(updateProfileImage(updatedUser.imagenDePerfil));
          onUpdateUser(updatedUser);
          onBack();
        },
        onEmailError: () => setEmailError(true),
        onError: (errorData) => {
          if (errorData?.errorType === "COMPANY_RFC_NOT_FOUND") {
            setRFCError(true);
          }
        },
      });
    } catch (error) {
      logger.error("Error:", { err: sanitizeError(error) });
    } finally {
      setLoading(false);
    }
  };

  /* --- Avatar and form field handlers --- */

  /**
   * Programmatically triggers the hidden file input click to choose an avatar image.
   * Used when user clicks the edit avatar button.
   */
  const handleAvatarEditClick = () => {
    fileInputRef.current.click();
  };

  /**
   * Handles avatar file selection. Accepts JPEG, PNG, WEBP and updates profile image and tempUser state.
   * @param {React.ChangeEvent<HTMLInputElement>} event - Change event from the file input.
   * Updates profile image state and tempUser; resets avatar-deleted flag.
   */
  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (
      file &&
      (file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/webp")
    ) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
        setProfileImageFormat(file.type.split("/")[1]);
        setTempUser((prev) => ({
          ...prev,
          imagenDePerfil: e.target.result,
          imagenDePerfilFormato: file.type.split("/")[1],
        }));
        setTempImage(e.target.result);
        setIsAvatarDeleted(false);
      };
      reader.readAsDataURL(file);
    } else {
      logger.error(
        "Invalid file type. Please upload a JPEG, PNG, or WEBP image.",
      );
    }
  };

  /**
   * Clears the profile image and sets avatar as deleted so the update sends empty image.
   * Resets imagenDePerfil and imagenDePerfilFormato in tempUser.
   */
  const handleDeleteAvatar = () => {
    setProfileImage(null);
    setProfileImageFormat("");
    setTempUser((prev) => ({
      ...prev,
      imagenDePerfil: "",
      imagenDePerfilFormato: "",
    }));
    setIsAvatarDeleted(true);
  };

  /**
   * Clears the value and related error state for the given field.
   * @param {string} field - Field key in tempUser (e.g. "nombre", "correoElectronico", "numeroCelular", "RFC").
   * Clears related error state for email, phone, and RFC when applicable.
   */
  const handleClearInput = (field) => {
    setTempUser((prev) => ({ ...prev, [field]: "" }));
    if (field === "correoElectronico") {
      setEmailError(false);
      setEmailFormatError(null);
    }
    if (field === "numeroCelular") {
      setPhoneFormatError(null);
    }
    if (field === "RFC") {
      setRfcFormatError(null);
    }
  };

  /**
   * Updates a single field in tempUser and clears its format error if applicable.
   * @param {string} field - Field key in tempUser.
   * @param {*} value - New value for the field.
   * Clears format error for that field when value changes.
   */
  const handleChange = (field, value) => {
    setTempUser((prev) => ({ ...prev, [field]: value }));
    if (field === "correoElectronico") {
      setEmailError(false);
      setEmailFormatError(null);
    }
    if (field === "numeroCelular") {
      setPhoneFormatError(null);
    }
    if (field === "RFC") {
      setRfcFormatError(null);
    }
  };

  /**
   * Checks if required fields are present and all filled fields pass format validation (and no RFC error from API).
   * @returns {boolean} True if the form can be submitted.
   * Requires email, user type, and RFC for primary user; checks format and API errors.
   */
  const isFormValid = () => {
    const isPrimaryUser = currentUserData?.esColaborador === false;
    if (
      !tempUser.correoElectronico?.trim() ||
      !tempUser.tipoDeUsuario ||
      (isPrimaryUser && !tempUser.RFC?.trim())
    ) {
      return false;
    }
    if (RFCError) {
      return false;
    }
    const emailResult = validateEmail(tempUser.correoElectronico);
    if (!emailResult.isValid) {
      return false;
    }
    if (tempUser.numeroCelular?.trim() !== "") {
      const phoneResult = validateMobilePhoneNumber(tempUser.numeroCelular);
      if (!phoneResult.isValid) {
        return false;
      }
    }
    if (tempUser.RFC?.trim() !== "") {
      const rfcResult = validateRFC(tempUser.RFC);
      if (!rfcResult.isValid) {
        return false;
      }
    }
    return true;
  };

  /* --- Effects (data loading and form init) --- */

  /**
   * Fetches current user data when the edited user reference changes (for permission checks).
   * Used to determine primary user and RFC field editability.
   */
  useEffect(() => {
    const fetchData = async () => {
      if (user) {
        await getUserData();
      }
    };

    fetchData();
  }, [user]);

  /**
   * Initializes the form from the given user when user changes.
   * Clears format errors and resets image key.
   */
  useEffect(() => {
    if (user) {
      setTempUser({
        tipoDeUsuario: user.tipoDeUsuario,
        nombre: user.nombre,
        titulo: user.titulo,
        correoElectronico: user.correoElectronico,
        numeroCelular: user.numeroCelular ?? "",
        RFC: user.RFC,
        imagenDePerfil: user.imagenDePerfil,
        imagenDePerfilFormato: user.imagenDePerfilFormato,
        activo: user.activo,
      });
      setProfileImage(user.imagenDePerfil);
      setProfileImageFormat(user.imagenDePerfilFormato);
      setRFCError(false);
      setEmailError(false);
      setEmailFormatError(null);
      setPhoneFormatError(null);
      setRfcFormatError(null);
      setImageKey(Date.now());
    }
  }, [user]);

  const isPrimaryUser = currentUserData?.esColaborador === false;
  const editor = currentUserData ?? currentUser;
  const editCaps = getUserIdEditCapabilities(editor, permissions);
  const isEditingSelf = currentUserData?._id === user?._id;
  const noEditOther = editCaps.canEditOnlySelf && !isEditingSelf;
  const limitedFieldsOnly =
    editCaps.canEditLimitedFields ||
    (editCaps.canEditOnlySelf && isEditingSelf);
  const emailDisabled = !editCaps.canEditEmail;
  const canEditTipoDeUsuario = editCaps.canEditTipoDeUsuario;
  const filteredRoles = useMemo(
    () => filterUserRolesForEdit(roles, canEditTipoDeUsuario, user?.RFC),
    [roles, canEditTipoDeUsuario, user?.RFC],
  );
  const displayedUserType = tempUser.tipoDeUsuario || user?.tipoDeUsuario;
  const roleOptions = useMemo(
    () =>
      rolesWithAssignedRole(filteredRoles, displayedUserType, user?.nombreRol),
    [filteredRoles, displayedUserType, user?.nombreRol],
  );
  const tipoDeUsuarioDisabled =
    isPrimaryUser || !canEditTipoDeUsuario || limitedFieldsOnly || noEditOther;
  const nombreDisabled = noEditOther;
  const tituloDisabled = noEditOther;
  const correoDisabled = emailDisabled || limitedFieldsOnly || noEditOther;
  const numeroCelularDisabled = noEditOther;
  const rfcDisabled = isPrimaryUser || limitedFieldsOnly || noEditOther;
  const avatarDisabled = noEditOther || (limitedFieldsOnly && !isEditingSelf);

  /**
   * Loads the role catalog only when this editor may change the user type.
   * A locked field uses the edited user's stored role name instead.
   */
  useEffect(() => {
    if (!editor?._id) {
      return undefined;
    }
    if (!canEditTipoDeUsuario) {
      setRoles([]);
      return undefined;
    }
    let cancelled = false;
    getUserRoles(navigate).then((list) => {
      if (!cancelled) {
        setRoles(list);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [editor?._id, canEditTipoDeUsuario, navigate]);

  /* --- Render --- */

  return (
    <div className={styles.userEditContainer}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        {/* Back navigation */}
        <div className={styles.backNavigation}>
          <button className={styles.backButton} onClick={onBack}>
            <ChevronLeftIcon />
            <span>{t("common.goBack")}</span>
          </button>
        </div>

        {/* Page title */}
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <SvgIcon sx={{ fontSize: "32px" }} viewBox="0 0 24 24">
              <defs>
                <linearGradient
                  id="gradient1"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#4eabe7" />
                  <stop offset="100%" stopColor="#2f59df" />
                </linearGradient>
              </defs>
              <EditOutlinedIcon sx={{ fill: "url(#gradient1)" }} />
            </SvgIcon>
            <h1 className={styles.title}>
              {t("components.userEditView.editUser")}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Avatar Section */}
        <div className={styles.avatarSection}>
          <div className={styles.avatarContainer}>
            <div className={styles.avatar}>
              {tempUser.imagenDePerfil ? (
                <AssetImage
                  src={tempImage || profilePic}
                  alt={t("components.userEditView.avatarAlt")}
                  className={styles.avatarImage}
                />
              ) : (
                tempUser.nombre.charAt(0)
              )}
            </div>
            {!avatarDisabled && (
              <div className={styles.avatarButtons}>
                <button
                  className={styles.avatarEditButton}
                  onClick={handleAvatarEditClick}
                >
                  <EditIcon />
                </button>
                <button
                  className={styles.avatarDeleteButton}
                  onClick={handleDeleteAvatar}
                >
                  <DeleteIcon />
                </button>
              </div>
            )}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept="image/jpeg, image/png, image/webp"
              onChange={handleAvatarChange}
            />
          </div>
        </div>

        {/* Form Content */}
        <div className={styles.formContent}>
          <div className={styles.formSection}>
            <label className={styles.formLabel}>
              {t("components.userEditView.userType")}
            </label>
            <Select
              options={roleOptions}
              value={selectedEditableRole(
                roleOptions,
                displayedUserType,
                user?.nombreRol,
              )}
              onChange={(selectedOption) =>
                handleChange("tipoDeUsuario", selectedOption.value)
              }
              placeholder={t("components.userEditView.userTypePlaceholder")}
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  borderRadius: "8px",
                  width: "100%",
                  height: "40px",
                  fontSize: "16px",
                  cursor: "pointer",
                  marginBottom: "10px",
                  borderColor: !tempUser.tipoDeUsuario
                    ? "red"
                    : baseStyles.borderColor,
                }),
                container: (baseStyles) => ({
                  ...baseStyles,
                }),
                valueContainer: (baseStyles) => ({
                  ...baseStyles,
                }),
                input: (baseStyles) => ({
                  ...baseStyles,
                }),
                option: (baseStyles) => ({
                  ...baseStyles,
                  cursor: "pointer",
                }),
                indicatorSeparator: () => ({ display: "none" }),
              }}
              isDisabled={tipoDeUsuarioDisabled}
            />
          </div>

          <div className={styles.formSection}>
            <label className={styles.formLabel}>
              {t("components.userEditView.userName")}
            </label>
            <div className={styles.inputContainer}>
              <div className={styles.inputWrapper}>
                <div className={styles.inputIcon}>
                  <UserNameIcon />
                </div>
                <input
                  type="text"
                  placeholder={t("components.userEditView.userNamePlaceholder")}
                  value={tempUser.nombre}
                  onChange={(e) => handleChange("nombre", e.target.value)}
                  className={styles.formInput}
                  disabled={nombreDisabled}
                />
                {tempUser.nombre && !nombreDisabled && (
                  <button
                    className={styles.inputClearButton}
                    onClick={() => handleClearInput("nombre")}
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
          </div>

          <hr className={styles.divider} />

          <div className={styles.formSection}>
            <label className={styles.formLabel}>
              {t("components.userEditView.title")}
            </label>
            <div className={styles.inputContainer}>
              <div className={styles.inputWrapper}>
                <div className={styles.inputIcon}>
                  <TitleIcon />
                </div>
                <input
                  type="text"
                  placeholder={t("components.userEditView.titlePlaceholder")}
                  value={tempUser.titulo}
                  onChange={(e) => handleChange("titulo", e.target.value)}
                  className={styles.formInput}
                  disabled={tituloDisabled}
                />
                {tempUser.titulo && !tituloDisabled && (
                  <button
                    className={styles.inputClearButton}
                    onClick={() => handleClearInput("titulo")}
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <label className={styles.formLabel}>
              {t("components.userEditView.email")}
            </label>
            <div className={styles.inputContainer}>
              <div className={styles.inputWrapper}>
                <div className={styles.inputIcon}>
                  <EmailIcon />
                </div>
                <input
                  type="email"
                  placeholder={t("components.userEditView.emailPlaceholder")}
                  value={tempUser.correoElectronico}
                  onChange={(e) =>
                    handleChange("correoElectronico", e.target.value)
                  }
                  onBlur={() => {
                    if (tempUser.correoElectronico.trim() === "") {
                      setEmailFormatError(null);
                      return;
                    }
                    const result = validateEmail(tempUser.correoElectronico);
                    setEmailFormatError(result.isValid ? null : result.error);
                  }}
                  className={styles.formInput}
                  style={{
                    borderColor:
                      tempUser.correoElectronico.trim() !== "" &&
                      (emailError || emailFormatError)
                        ? "red"
                        : "",
                  }}
                  disabled={correoDisabled}
                />
                {tempUser.correoElectronico && !correoDisabled && (
                  <button
                    className={styles.inputClearButton}
                    onClick={() => handleClearInput("correoElectronico")}
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <label className={styles.formLabel}>
              {t("components.userEditView.phone")}
            </label>
            <div className={styles.inputContainer}>
              <div className={styles.inputWrapper}>
                <input
                  type="tel"
                  placeholder={t("components.userEditView.phonePlaceholder")}
                  value={tempUser.numeroCelular}
                  onChange={(e) =>
                    handleChange("numeroCelular", e.target.value)
                  }
                  onBlur={() => {
                    if (tempUser.numeroCelular?.trim() === "") {
                      setPhoneFormatError(null);
                      return;
                    }
                    const result = validateMobilePhoneNumber(
                      tempUser.numeroCelular,
                    );
                    setPhoneFormatError(result.isValid ? null : result.error);
                  }}
                  className={styles.formInput}
                  style={{
                    borderColor:
                      tempUser.numeroCelular?.trim() !== "" && phoneFormatError
                        ? "red"
                        : "",
                  }}
                  disabled={numeroCelularDisabled}
                />
                {tempUser.numeroCelular && !numeroCelularDisabled && (
                  <button
                    className={styles.inputClearButton}
                    onClick={() => handleClearInput("numeroCelular")}
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className={styles.formSection}>
            <label className={styles.formLabel}>
              {t("components.userEditView.rfc")}
            </label>
            <div className={styles.inputContainer}>
              <div className={styles.inputWrapper}>
                <div className={styles.inputIcon}>
                  <RFCIcon />
                </div>
                <input
                  type="text"
                  placeholder={t("components.userEditView.rfcPlaceholder")}
                  value={tempUser.RFC}
                  onChange={(e) => handleChange("RFC", e.target.value)}
                  onBlur={() => {
                    if (tempUser.RFC?.trim() === "") {
                      setRfcFormatError(null);
                      return;
                    }
                    const result = validateRFC(tempUser.RFC);
                    setRfcFormatError(result.isValid ? null : result.error);
                  }}
                  className={styles.formInput}
                  style={{
                    borderColor:
                      tempUser.RFC?.trim() !== "" &&
                      (RFCError || rfcFormatError)
                        ? "red"
                        : "",
                  }}
                  disabled={rfcDisabled}
                />
                {tempUser.RFC && !rfcDisabled && (
                  <button
                    className={styles.inputClearButton}
                    onClick={() => handleClearInput("RFC")}
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.actionButtons}>
            <button onClick={onBack} className={styles.cancelButton}>
              {t("components.userEditView.cancel")}
            </button>
            <button
              className={styles.saveButton}
              onClick={handleUpdateUser}
              disabled={loading || !isFormValid() || noEditOther}
            >
              {loading ? (
                <CircularProgress size={24} />
              ) : (
                t("components.userEditView.save")
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserEditView;
